import styled from "styled-components";
import { tokens } from "../../../public/tokens";

const { colors, typography, globals } = tokens;

/**
 * Base style shared by every data-row cell, fixed at Figma's 56px row height so header rows
 * (unfixed height) stay visually distinct from body rows. Padding is horizontal only — adding
 * vertical padding would stack on top of the fixed height instead of forming it.
 */
const dataCell = {
    height: "56px",
    padding: `0 ${globals["spacing-2"]}`,
    border: "none",
    borderBottom: `${globals["spacing-px"]} solid ${colors.surfaces["border-divider"]}`,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.regular,
    color: colors.text.primary,
    verticalAlign: "middle",
    wordBreak: "break-word",

    "&:first-child": {
        paddingLeft: globals["spacing-4"],
    },
};

export const StyledDataTableRoot = styled.div({
    width: "100%",
});

export const StyledTableWrapper = styled.div({
    width: "100%",
    overflowX: "auto",
});

export const StyledTable = styled.table({
    width: "100%",
    border: "none",
    borderCollapse: "collapse",

    "tbody tr:nth-of-type(even)": {
        backgroundColor: colors.surfaces["page-background"],
    },
});

export const StyledTh = styled.th(({ $width, $align = "left", $sortable = false }) => ({
    textAlign: $align,
    width: $width,
    padding: `${globals["spacing-2-5"]} ${globals["spacing-2"]}`,
    border: "none",
    borderBottom: `${globals["spacing-px"]} solid ${colors.surfaces["border-divider"]}`,
    color: colors.text.primary,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
    whiteSpace: "nowrap",
    cursor: $sortable ? "pointer" : "default",
    userSelect: "none",

    "&:first-child": {
        paddingLeft: globals["spacing-4"],
    },
}));

export const StyledSortIcon = styled.i(({ $active = false }) => ({
    marginLeft: globals["spacing-2-5"],
    fontSize: typography.fontSize["md-12px"],
    color: $active ? colors.text.primary : colors.text.disabled,
}));

export const StyledHeaderIcon = styled.i({
    marginRight: globals["spacing-2-5"],
    fontSize: typography.fontSize["lg-14px"],
    color: colors.text.primary,
});

export const StyledTd = styled.td(({ $align = "left" }) => ({
    ...dataCell,
    textAlign: $align,
}));

export const StyledMessageCell = styled.td({
    ...dataCell,
    height: "auto",
    padding: globals["spacing-5"],
    textAlign: "center",
    color: colors.text.secondary,
});

/**
 * Fixes a `filterSlot` control (e.g. a SelectDropdown) to a consistent toolbar width — FilterBar
 * renders its children at their natural width otherwise, which varies with each option's label.
 */
export const StyledFilterSlotItem = styled.div({
    width: "100px",
});

export const StyledExpandableList = styled.ul({
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: globals["spacing-1"],
});

export const StyledExpandableListItem = styled.li({
    display: "flex",
    alignItems: "flex-start",
    gap: globals["spacing-1-5"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    color: colors.text.primary,

    "&::before": {
        content: '"•"',
        color: colors.text.link,
        fontWeight: typography.fontWeights.bold,
        lineHeight: typography.lineHeights["100"],
    },
});

export const StyledExpandableListToggle = styled.button({
    alignSelf: "flex-start",
    margin: 0,
    padding: 0,
    border: "none",
    background: "none",
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.semibold,
    color: colors.text.link,
    cursor: "pointer",
});
